import { Analysis, normalizeTechnicalState } from './analysis.model';

/**
 * Execution-state/stage display logic shared between `AnalysisTable` and any
 * other list that shows an analysis's technical pipeline status (e.g. the
 * dashboard's failed-analyses widget) — one place to keep labels/colors in
 * sync instead of every list re-deriving its own.
 */

/** Flat column wins over the nested technicalStatus blob — campaign rows only carry the flat one. */
export function getExecutionState(a: Analysis): string {
  return a.technicalState ?? a.technicalStatus?.state ?? 'draft';
}

export function getExecutionStage(a: Analysis): string {
  return a.technicalStage ?? a.technicalStatus?.stage ?? 'idle';
}

export function isExecutionRunning(a: Analysis): boolean {
  const state = normalizeTechnicalState(getExecutionState(a));
  return state === 'processing' || state === 'queued';
}

export function getTechnicalStateLabel(state: string | undefined): string {
  switch (normalizeTechnicalState(state)) {
    case 'completed': return 'Abgeschlossen';
    case 'processing': return 'Läuft';
    case 'queued': return 'Warteschlange';
    case 'failed': return 'Fehler';
    default: return 'Entwurf';
  }
}

export function getTechnicalStageLabel(stage: string | undefined): string {
  switch (stage) {
    case 'geocut': return 'Geodaten';
    case 'analyzing': return 'Analyse';
    case 'planning': return 'Planung';
    case 'complete': return 'Fertig';
    default: return 'Bereit';
  }
}

/**
 * Monochrome by design: the pipeline states are a sequence, not categories, so
 * hue carries no information here — weight does. Only a failure keeps a colour,
 * because that is the one state a user has to spot without reading.
 */
/** Execution cell (guide v1.1, Tables): an icon and the word, no badge. */
export function getExecutionIndicator(state: string | undefined): { icon: string; class: string } {
  switch (normalizeTechnicalState(state)) {
    case 'completed': return { icon: 'lucideCircleCheck', class: 'text-success' };
    case 'processing':
    case 'queued': return { icon: 'lucideLoaderCircle', class: 'animate-spin text-primary' };
    case 'failed': return { icon: 'lucideCircleX', class: 'text-danger' };
    default: return { icon: 'lucideCircleDashed', class: 'text-muted-foreground' };
  }
}

/**
 * "Läuft · Analyse" — the stage is appended only when it carries information the
 * state does not already imply. `idle` pairs with a draft and `complete` with a
 * finished run, so in those cases the state alone says everything.
 */
export function formatExecutionLabel(
  state: string | undefined,
  stage: string | undefined,
): string {
  const stateLabel = getTechnicalStateLabel(state);
  if (stage === 'idle' || stage === 'complete') return stateLabel;
  return `${stateLabel} · ${getTechnicalStageLabel(stage)}`;
}

export function getExecutionLabel(a: Analysis): string {
  return formatExecutionLabel(getExecutionState(a), getExecutionStage(a));
}
